/* global HTMLElement, Image, URLSearchParams, AbortController */
/**
 * Drezanielle Virtual Try-On — storefront block.
 *
 * Implemented as a custom element (<drezanielle-vto>) so it survives Fabric's
 * DOM morphing (section rendering): the browser re-runs connectedCallback
 * whenever the element is (re)inserted, and disconnectedCallback lets us
 * release listeners and object URLs. Every document-level listener is scoped
 * by product id so multiple blocks on one page never cross-talk.
 *
 * i18n: all customer-visible copy comes from the <script data-vto-locales>
 * JSON dictionary rendered by the Liquid block from the active locale.
 * _t(key, vars) walks the nested dictionary and interpolates placeholders in
 * both Shopify ({{ name }}) and single-brace ({name}) syntax.
 */
(function () {
  "use strict";

  var TAG = "drezanielle-vto";
  var MAX_PHOTO_BYTES = 10 * 1024 * 1024;
  var MIN_PHOTO_DIMENSION = 512;
  // Keep the browser slightly ahead of the provider's five-minute ceiling so
  // the server can return its authoritative error and refund state.
  var SUBMIT_TIMEOUT_MS = 630000;
  var POLL_INTERVAL_MS = 3000;
  var STORED_JOB_KEY = "drezanielle:vto:active-job:v1";
  var STORED_JOB_TTL_MS = 30 * 60 * 1000;
  var GLOBAL_JOB_ID = "drezanielle-vto-global-job";
  var ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

  var GLOBAL_COPY = {
    en: {
      eyebrow: "PRIVATE FITTING ROOM",
      processingTitle: "Your fitting is being tailored",
      processingBody: "Keep browsing — we’ll let you know when it’s ready.",
      processingAction: "Return to fitting",
      readyTitle: "Your fitting is ready",
      readyBody: "Open the product to view and download your preview.",
      readyAction: "View result",
      failedTitle: "Your fitting could not be completed",
      failedBody: "Your fitting was not charged. You can try again from the product page.",
      failedAction: "Try again",
      dismiss: "Dismiss"
    },
    "zh-CN": {
      eyebrow: "私人试衣间",
      processingTitle: "正在为您制作试穿效果",
      processingBody: "您可以继续浏览，完成后我们会在页面中提示您。",
      processingAction: "返回试穿",
      readyTitle: "您的试穿效果已完成",
      readyBody: "返回商品页即可查看和下载试穿结果。",
      readyAction: "查看结果",
      failedTitle: "本次试穿未能完成",
      failedBody: "本次未扣除额度，您可以返回商品页重试。",
      failedAction: "返回重试",
      dismiss: "关闭"
    },
    de: {
      eyebrow: "PRIVATE ANPROBE",
      processingTitle: "Ihre Anprobe wird angefertigt",
      processingBody: "Sie können weiterstöbern. Wir melden uns, sobald sie fertig ist.",
      processingAction: "Zur Anprobe",
      readyTitle: "Ihre Anprobe ist fertig",
      readyBody: "Öffnen Sie das Produkt, um die Vorschau anzusehen und herunterzuladen.",
      readyAction: "Ergebnis ansehen",
      failedTitle: "Die Anprobe konnte nicht abgeschlossen werden",
      failedBody: "Es wurde nichts berechnet. Sie können es auf der Produktseite erneut versuchen.",
      failedAction: "Erneut versuchen",
      dismiss: "Schließen"
    },
    fr: {
      eyebrow: "CABINE PRIVÉE",
      processingTitle: "Votre essayage est en préparation",
      processingBody: "Continuez votre visite. Nous vous préviendrons dès qu’il sera prêt.",
      processingAction: "Retour à l’essayage",
      readyTitle: "Votre essayage est prêt",
      readyBody: "Ouvrez le produit pour voir et télécharger votre aperçu.",
      readyAction: "Voir le résultat",
      failedTitle: "L’essayage n’a pas pu être terminé",
      failedBody: "Aucun crédit n’a été débité. Vous pouvez réessayer depuis la page produit.",
      failedAction: "Réessayer",
      dismiss: "Fermer"
    },
    ja: {
      eyebrow: "プライベート試着室",
      processingTitle: "試着イメージを仕立てています",
      processingBody: "そのままお買い物をお楽しみください。完成時にお知らせします。",
      processingAction: "試着に戻る",
      readyTitle: "試着イメージが完成しました",
      readyBody: "商品ページで結果の確認とダウンロードができます。",
      readyAction: "結果を見る",
      failedTitle: "試着を完了できませんでした",
      failedBody: "回数は消費されていません。商品ページから再度お試しください。",
      failedAction: "もう一度試す",
      dismiss: "閉じる"
    },
    ko: {
      eyebrow: "프라이빗 피팅룸",
      processingTitle: "가상 피팅을 제작하고 있습니다",
      processingBody: "계속 둘러보셔도 됩니다. 완료되면 알려드리겠습니다.",
      processingAction: "피팅으로 돌아가기",
      readyTitle: "가상 피팅이 준비되었습니다",
      readyBody: "상품 페이지에서 결과를 확인하고 다운로드하세요.",
      readyAction: "결과 보기",
      failedTitle: "피팅을 완료하지 못했습니다",
      failedBody: "이용 횟수는 차감되지 않았습니다. 상품 페이지에서 다시 시도하세요.",
      failedAction: "다시 시도",
      dismiss: "닫기"
    },
    ar: {
      eyebrow: "غرفة القياس الخاصة",
      processingTitle: "يتم تجهيز تجربة القياس",
      processingBody: "يمكنك متابعة التصفح، وسنخبرك عند اكتمالها.",
      processingAction: "العودة إلى القياس",
      readyTitle: "تجربة القياس جاهزة",
      readyBody: "افتح صفحة المنتج لعرض النتيجة وتنزيلها.",
      readyAction: "عرض النتيجة",
      failedTitle: "تعذر إكمال تجربة القياس",
      failedBody: "لم يتم خصم المحاولة. يمكنك المحاولة مرة أخرى من صفحة المنتج.",
      failedAction: "المحاولة مجددًا",
      dismiss: "إغلاق"
    }
  };

  // API error code -> dictionary key. Not customer-visible text, just the
  // lookup table; the copy itself lives in the locale files.
  var ERROR_KEYS = {
    QUOTA_EXHAUSTED: "errors.quota_exhausted",
    REQUEST_IN_PROGRESS: "errors.request_in_progress",
    PROVIDER_BUSY: "errors.provider_busy",
    PROVIDER_UNAVAILABLE: "errors.provider_busy",
    CATALOG_UNAVAILABLE: "errors.provider_busy",
    FEATURE_DISABLED: "errors.feature_disabled",
    PROVIDER_NOT_CONFIGURED: "errors.feature_disabled",
    APP_NOT_INSTALLED: "errors.feature_disabled",
    PHOTO_TOO_LARGE: "errors.photo_too_large",
    PROVIDER_PAYLOAD_REJECTED: "errors.photo_payload_rejected",
    PHOTO_FORMAT_UNSUPPORTED: "errors.photo_type",
    PHOTO_BYTES_INVALID: "errors.photo_undecodable",
    TRY_ON_INPUT_INVALID: "errors.input_invalid",
    VARIANT_NOT_IN_PRODUCT: "errors.item_unavailable",
    PRODUCT_NOT_FOUND: "errors.item_unavailable",
    PRODUCT_UNAVAILABLE: "errors.item_unavailable",
    GARMENT_IMAGE_MISSING: "errors.item_unavailable",
    RESULT_EXPIRED: "errors.result_expired"
  };

  function makeIdempotencyKey() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") return window.crypto.randomUUID();
    var alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789._~-";
    var key = "";
    for (var i = 0; i < 32; i += 1) key += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
    return key;
  }

  function activeGlobalCopy() {
    var raw = String(document.documentElement.lang || "en");
    if (/^zh/i.test(raw)) return GLOBAL_COPY["zh-CN"];
    var short = raw.split("-")[0];
    return GLOBAL_COPY[short] || GLOBAL_COPY.en;
  }

  function readStoredJob() {
    try {
      var raw = window.localStorage.getItem(STORED_JOB_KEY);
      if (!raw) return null;
      var job = JSON.parse(raw);
      if (!job || typeof job.requestId !== "string" || typeof job.startedAt !== "number") return null;
      if (Date.now() - job.startedAt > STORED_JOB_TTL_MS) {
        window.localStorage.removeItem(STORED_JOB_KEY);
        return null;
      }
      return job;
    } catch {
      return null;
    }
  }

  function writeStoredJob(job) {
    try { window.localStorage.setItem(STORED_JOB_KEY, JSON.stringify(job)); } catch { /* Storage can be disabled. */ }
  }

  function clearStoredJob(requestId) {
    var job = readStoredJob();
    if (!job || !requestId || job.requestId === requestId) {
      try { window.localStorage.removeItem(STORED_JOB_KEY); } catch { /* Storage can be disabled. */ }
    }
  }

  function safeJobUrl(job) {
    try {
      var target = new URL(job.productUrl || "/", window.location.origin);
      if (target.origin !== window.location.origin) return "/";
      target.searchParams.set("vto_request", job.requestId);
      return target.pathname + target.search + target.hash;
    } catch {
      return "/";
    }
  }

  function renderGlobalJob(job, status) {
    var copy = activeGlobalCopy();
    var root = document.getElementById(GLOBAL_JOB_ID);
    if (!root) {
      root = document.createElement("aside");
      root.id = GLOBAL_JOB_ID;
      root.className = "drezanielle-vto-global-job";
      root.setAttribute("role", "status");
      root.setAttribute("aria-live", "polite");

      var mark = document.createElement("span");
      mark.className = "drezanielle-vto-global-job__mark";
      mark.setAttribute("aria-hidden", "true");
      root.appendChild(mark);

      var copyWrap = document.createElement("span");
      copyWrap.className = "drezanielle-vto-global-job__copy";
      var eyebrow = document.createElement("span");
      eyebrow.className = "drezanielle-vto-global-job__eyebrow";
      var title = document.createElement("strong");
      title.className = "drezanielle-vto-global-job__title";
      var body = document.createElement("span");
      body.className = "drezanielle-vto-global-job__body";
      copyWrap.append(eyebrow, title, body);
      root.appendChild(copyWrap);

      var action = document.createElement("a");
      action.className = "drezanielle-vto-global-job__action";
      root.appendChild(action);

      var dismiss = document.createElement("button");
      dismiss.type = "button";
      dismiss.className = "drezanielle-vto-global-job__dismiss";
      dismiss.textContent = "×";
      dismiss.addEventListener("click", function () { root.hidden = true; });
      root.appendChild(dismiss);
      document.body.appendChild(root);
    }

    root.hidden = false;
    root.dataset.status = status;
    root.dir = /^ar/i.test(String(document.documentElement.lang || "")) ? "rtl" : "ltr";
    root.querySelector(".drezanielle-vto-global-job__eyebrow").textContent = copy.eyebrow;
    root.querySelector(".drezanielle-vto-global-job__title").textContent = copy[status + "Title"];
    root.querySelector(".drezanielle-vto-global-job__body").textContent = copy[status + "Body"];
    var currentAction = root.querySelector(".drezanielle-vto-global-job__action");
    currentAction.textContent = copy[status + "Action"];
    currentAction.href = safeJobUrl(job);
    root.querySelector(".drezanielle-vto-global-job__dismiss").setAttribute("aria-label", copy.dismiss);
  }

  var globalMonitorStarted = false;
  async function monitorStoredJob() {
    var job = readStoredJob();
    if (!job || globalMonitorStarted) return;
    var matchingBlock = Array.prototype.find.call(document.querySelectorAll(TAG), function (element) {
      return String(element.dataset.productId || "") === String(job.productId || "");
    });
    if (matchingBlock && typeof matchingBlock._resumeStoredJob === "function") {
      matchingBlock._resumeStoredJob(job);
      return;
    }

    globalMonitorStarted = true;
    if (job.status === "ready") {
      renderGlobalJob(job, "ready");
      return;
    }
    renderGlobalJob(job, "processing");
    while (Date.now() - job.startedAt <= STORED_JOB_TTL_MS) {
      try {
        var response = await window.fetch("/apps/drezanielle-try-on/api/v1/try-on/" + encodeURIComponent(job.requestId), {
          headers: { Accept: "application/json" },
          credentials: "same-origin"
        });
        var body = await response.json().catch(function () { return {}; });
        if (response.ok && body.status === "succeeded" && body.imageDataUrl) {
          job.status = "ready";
          writeStoredJob(job);
          renderGlobalJob(job, "ready");
          return;
        }
        if (!response.ok || body.status === "failed") {
          clearStoredJob(job.requestId);
          renderGlobalJob(job, "failed");
          return;
        }
      } catch {
        // A page transition or transient storefront request should not lose
        // the job. Keep the atelier marker and retry on the next interval.
      }
      await new Promise(function (resolve) { window.setTimeout(resolve, POLL_INTERVAL_MS); });
    }
    clearStoredJob(job.requestId);
    renderGlobalJob(job, "failed");
  }

  function modalWidthUrl(url) {
    return typeof url === "string" && url.indexOf("width=1600") !== -1 ? url.replace("width=1600", "width=900") : url;
  }

  function normalizeImageUrl(url) {
    if (typeof url === "string" && url.indexOf("//") === 0) return window.location.protocol + url;
    return url;
  }

  function decodeViaImageElement(file) {
    return new Promise(function (resolve) {
      var url = window.URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        var dimensions = { width: img.naturalWidth, height: img.naturalHeight };
        window.URL.revokeObjectURL(url);
        resolve(dimensions);
      };
      img.onerror = function () {
        window.URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });
  }

  function decodePhoto(file) {
    if (typeof window.createImageBitmap === "function") {
      return window.createImageBitmap(file).then(function (bitmap) {
        var dimensions = { width: bitmap.width, height: bitmap.height };
        if (typeof bitmap.close === "function") bitmap.close();
        return dimensions;
      }).catch(function () {
        return decodeViaImageElement(file);
      });
    }
    return decodeViaImageElement(file);
  }

  class DrezanielleVto extends HTMLElement {
    constructor() {
      super();
      this._state = {
        file: null,
        previewUrl: null,
        idempotencyKey: null,
        busy: false,
        lastResult: null,
        sessionLoaded: false,
        quota: null,
        variantMap: {},
        fileToken: 0
      };
      this._locales = {};
      this._initialized = false;
      this._timers = { timeout: null, elapsed: null };
      this._abort = null;
      this._bound = {};
      this.$ = {};
      this._shellAnim = null;
      this._closing = false;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      var $ = this.$;
      $.launch = this.querySelector("[data-vto-launch]");
      $.launchLabel = this.querySelector("[data-vto-launch-label]");
      $.entryQuota = this.querySelector("[data-vto-entry-quota]");
      $.dialog = this.querySelector(".drezanielle-vto__dialog");
      $.close = this.querySelector("[data-vto-close]");
      $.garmentImage = this.querySelector("[data-vto-garment-image]");
      $.garmentPlaceholder = this.querySelector("[data-vto-garment-placeholder]");
      $.garmentTitle = this.querySelector("[data-vto-garment-title]");
      $.garmentSku = this.querySelector("[data-vto-garment-sku]");
      $.currentVariant = this.querySelector("[data-vto-current-variant]");
      $.quota = this.querySelector("[data-vto-quota]");
      $.form = this.querySelector("[data-vto-form]");
      $.dropzone = this.querySelector("[data-vto-dropzone]");
      $.fileInput = this.querySelector("[data-vto-file]");
      $.fileLabel = this.querySelector("[data-vto-file-label]");
      $.preview = this.querySelector("[data-vto-preview]");
      $.photoNote = this.querySelector("[data-vto-photo-note]");
      $.consent = this.querySelector("[data-vto-consent]");
      $.error = this.querySelector("[data-vto-error]");
      $.progress = this.querySelector("[data-vto-progress]");
      $.progressStatus = this.querySelector("[data-vto-progress-status]");
      $.waiting = this.querySelector("[data-vto-waiting]");
      $.waitSeconds = this.querySelector("[data-vto-wait-seconds]");
      $.waitStage = this.querySelector("[data-vto-wait-stage]");
      $.waitClose = this.querySelector("[data-vto-wait-close]");
      $.waitSteps = this.querySelectorAll("[data-vto-wait-step]");
      $.submit = this.querySelector("[data-vto-submit]");
      $.submitLabel = this.querySelector("[data-vto-submit-label]");
      $.result = this.querySelector("[data-vto-result]");
      $.resultNote = this.querySelector("[data-vto-result-note]");
      $.resultImage = this.querySelector("[data-vto-result-image]");
      $.resultPhoto = this.querySelector("[data-vto-result-photo]");
      $.download = this.querySelector("[data-vto-download]");
      $.again = this.querySelector("[data-vto-again]");
      $.toast = this.querySelector("[data-vto-toast]");
      $.toastTitle = this.querySelector("[data-vto-toast-title]");
      $.toastBody = this.querySelector("[data-vto-toast-body]");
      $.toastAction = this.querySelector("[data-vto-toast-action]");
      $.toastDismiss = this.querySelector("[data-vto-toast-dismiss]");

      if (!$.launch || !$.dialog || !$.form || !$.fileInput || !$.submit) return;

      var localeScript = this.querySelector("script[data-vto-locales]");
      if (localeScript) {
        try { this._locales = JSON.parse(localeScript.textContent || "{}"); } catch { this._locales = {}; }
      }

      this._state.variantMap = this._parseVariants();

      this._bound.onLaunch = this._onLaunch.bind(this);
      this._bound.onCloseRequest = this._closeDialog.bind(this);
      this._bound.onDialogClosed = this._onDialogClosed.bind(this);
      this._bound.onDialogCancel = this._onDialogCancel.bind(this);
      this._bound.onDialogClick = this._onDialogClick.bind(this);
      this._bound.onFileChange = this._onFileInput.bind(this);
      this._bound.onDrop = this._onDrop.bind(this);
      this._bound.onDragState = this._onDragState.bind(this);
      this._bound.onConsent = this._updateSubmitState.bind(this);
      this._bound.onSubmit = this._onSubmit.bind(this);
      this._bound.onAgain = this._resetFitting.bind(this);
      this._bound.onWaitClose = this._closeDialog.bind(this);
      this._bound.onToastAction = this._onToastAction.bind(this);
      this._bound.onToastDismiss = this._hideToast.bind(this);
      this._bound.onProductSelect = this._onProductSelect.bind(this);
      this._bound.onHiddenIdChange = this._onHiddenIdChange.bind(this);

      $.launch.addEventListener("click", this._bound.onLaunch);
      if ($.close) $.close.addEventListener("click", this._bound.onCloseRequest);
      $.dialog.addEventListener("close", this._bound.onDialogClosed);
      $.dialog.addEventListener("cancel", this._bound.onDialogCancel);
      $.dialog.addEventListener("click", this._bound.onDialogClick);
      $.fileInput.addEventListener("change", this._bound.onFileChange);
      $.form.addEventListener("submit", this._bound.onSubmit);
      if ($.consent) $.consent.addEventListener("change", this._bound.onConsent);
      if ($.again) $.again.addEventListener("click", this._bound.onAgain);
      if ($.waitClose) $.waitClose.addEventListener("click", this._bound.onWaitClose);
      if ($.toastAction) $.toastAction.addEventListener("click", this._bound.onToastAction);
      if ($.toastDismiss) $.toastDismiss.addEventListener("click", this._bound.onToastDismiss);
      if ($.dropzone) {
        ["dragenter", "dragover"].forEach((name) => {
          $.dropzone.addEventListener(name, this._bound.onDragState);
        });
        ["dragleave", "drop"].forEach((name) => {
          $.dropzone.addEventListener(name, this._bound.onDragState);
        });
        $.dropzone.addEventListener("drop", this._bound.onDrop);
      }
      document.addEventListener("shopify:product:select", this._bound.onProductSelect);
      document.addEventListener("change", this._bound.onHiddenIdChange);

      // Deep link: ?variant=<id> wins over the server-rendered default.
      var urlVariant = null;
      try { urlVariant = new URLSearchParams(window.location.search).get("variant"); } catch { urlVariant = null; }
      if (urlVariant && this._state.variantMap[String(urlVariant)]) this._applyVariant(String(urlVariant));

      if (this.dataset.customerLoggedIn === "true") {
        this._loadSession();
      } else {
        this._renderLoggedOut();
      }
    }

    disconnectedCallback() {
      document.removeEventListener("shopify:product:select", this._bound.onProductSelect);
      document.removeEventListener("change", this._bound.onHiddenIdChange);
      this._clearTimers();
      if (this._abort) this._abort.abort();
      if (this._shellAnim) {
        this._shellAnim.cancel();
        this._shellAnim = null;
      }
      this._revokePreview();
    }

    /* -------------------------------------------------------------- i18n */

    _t(key, vars) {
      var value = this._locales;
      var parts = key.split(".");
      for (var i = 0; i < parts.length; i += 1) {
        if (!value || typeof value !== "object") { value = null; break; }
        value = value[parts[i]];
      }
      if (typeof value !== "string") return key;
      if (!vars) return value;
      return value.replace(/\{\{\s*(\w+)\s*\}\}|\{(\w+)\}/g, function (match, doubleBrace, singleBrace) {
        var name = doubleBrace || singleBrace;
        return vars[name] != null ? String(vars[name]) : match;
      });
    }

    /* ------------------------------------------------------------- state */

    _parseVariants() {
      var map = {};
      var list = [];
      try { list = JSON.parse(this.getAttribute("data-variants") || "[]"); } catch { list = []; }
      if (!Array.isArray(list)) list = [];
      list.forEach(function (variant) {
        if (variant && variant.id != null) map[String(variant.id)] = variant;
      });
      return map;
    }

    _apiUrl(path) {
      var base = this.getAttribute("data-api-base") || "/apps/drezanielle-try-on";
      return base.replace(/\/$/, "") + path;
    }

    _redirectToLogin() {
      var notice = this._t("entry.login_redirect");
      this._setEntryQuota(notice);
      this._showError(notice);
      var loginUrl = this.getAttribute("data-login-url") || "/account/login";
      var target = new URL(loginUrl, window.location.origin);
      target.searchParams.set("return_url", window.location.pathname + window.location.search);
      window.location.assign(target.toString());
    }

    /* ------------------------------------------------------------ session */

    async _loadSession() {
      try {
        var response = await window.fetch(this._apiUrl("/api/v1/session"), {
          headers: { Accept: "application/json" },
          credentials: "same-origin"
        });
        var body = await response.json().catch(function () { return {}; });
        if (response.status === 401 || (body.error && body.error.code === "CUSTOMER_LOGIN_REQUIRED")) {
          this.dataset.customerLoggedIn = "false";
          this._renderLoggedOut();
          return;
        }
        if (!response.ok) {
          this._setEntryQuota(this._t("quota.unavailable"));
          return;
        }
        if (body.features && body.features.tryOnEnabled === false) {
          this.hidden = true;
          return;
        }
        this._state.sessionLoaded = true;
        if (body.quota) this._setQuota(body.quota);
        var storedJob = readStoredJob();
        if (storedJob && String(storedJob.productId || "") === String(this.dataset.productId || "")) {
          this._resumeStoredJob(storedJob);
        }
      } catch {
        this._setEntryQuota(this._t("quota.unavailable"));
      }
    }

    _renderLoggedOut() {
      this._setEntryQuota(this._t("entry.sign_in_hint"));
    }

    _setEntryQuota(text) {
      if (!this.$.entryQuota) return;
      this.$.entryQuota.textContent = text;
      this.$.entryQuota.hidden = !text;
    }

    _setQuota(quota) {
      if (!quota) return;
      var remaining = Number(quota.remaining);
      if (!Number.isFinite(remaining)) return;
      this._state.quota = quota;
      var text = remaining === 1
        ? this._t("quota.remaining_one", { count: remaining })
        : this._t("quota.remaining_other", { count: remaining });
      this._setEntryQuota(text);
      if (this.$.quota) this.$.quota.textContent = text;
    }

    /* ------------------------------------------------------ variant watch */

    _productIdFromEvent(event) {
      if (event.product && event.product.id != null) return String(event.product.id);
      var target = event.target;
      var picker = target && typeof target.closest === "function" ? target.closest("variant-picker") : null;
      if (picker && picker.getAttribute("data-product-id")) return String(picker.getAttribute("data-product-id"));
      return null;
    }

    _onProductSelect(event) {
      var productId = this._productIdFromEvent(event);
      if (productId == null || productId !== String(this.dataset.productId)) return;
      var variantId = event.detail && event.detail.variantId != null ? String(event.detail.variantId) : null;
      if (variantId) this._applyVariant(variantId);
      if (event.promise && typeof event.promise.then === "function") {
        event.promise.then((payload) => {
          if (!payload) return;
          var detailProductId = payload.detail && payload.detail.productId != null ? String(payload.detail.productId) : null;
          if (detailProductId != null && detailProductId !== String(this.dataset.productId)) return;
          var resource = payload.resource;
          if (resource && resource.id != null && String(resource.id) === String(this.dataset.variantId)) {
            this._reconcileVariant(resource);
          }
        }).catch(() => { /* the synchronous update already ran */ });
      }
    }

    _onHiddenIdChange(event) {
      var target = event.target;
      if (!target || target.name !== "id") return;
      if (this.contains(target)) return;
      var id = String(target.value || "");
      if (!id) return;
      var scope = typeof target.closest === "function" ? target.closest("[data-product-id]") : null;
      var scopeMatches = scope && String(scope.getAttribute("data-product-id")) === String(this.dataset.productId);
      if (!scopeMatches && !this._state.variantMap[id]) return;
      this._applyVariant(id);
    }

    _applyVariant(variantId) {
      var id = String(variantId);
      var entry = this._state.variantMap[id] || null;
      this.dataset.variantId = id;
      var sku = entry && entry.sku ? String(entry.sku) : "";
      this.dataset.sku = sku;
      var title = entry && entry.title ? String(entry.title) : "";
      if (title) this.dataset.variantTitle = title;
      var image = entry && entry.image ? String(entry.image) : "";
      if (image) this.dataset.referenceImage = image;
      this._state.idempotencyKey = null;
      this._renderVariant(title, sku, image);
    }

    _reconcileVariant(resource) {
      var sku = resource.sku ? String(resource.sku) : null;
      if (sku != null) this.dataset.sku = sku;
      var title = resource.title || resource.name || this.dataset.variantTitle || "";
      if (title) this.dataset.variantTitle = String(title);
      var image = null;
      if (resource.featured_media && resource.featured_media.preview_image && resource.featured_media.preview_image.src) {
        image = normalizeImageUrl(resource.featured_media.preview_image.src);
        this.dataset.referenceImage = image;
      }
      this._renderVariant(String(title || ""), sku, image);
    }

    _renderVariant(title, sku, image) {
      var $ = this.$;
      var productTitle = this.dataset.productTitle || "";
      // "Default Title" is Shopify's sentinel for products without options —
      // it is data, not copy, and always arrives in English.
      var hasTitle = Boolean(title) && title !== "Default Title";
      if ($.garmentTitle) $.garmentTitle.textContent = productTitle;
      if ($.garmentSku) $.garmentSku.textContent = sku ? this._t("garment.sku_prefix") + " " + sku : "";
      if ($.currentVariant) {
        $.currentVariant.textContent = hasTitle ? this._t("garment.current_variant", { title: title }) : "";
      }
      if ($.garmentImage) {
        if (image) {
          $.garmentImage.src = modalWidthUrl(image);
          $.garmentImage.alt = hasTitle ? this._t("garment.image_alt", { product: productTitle, variant: title }) : productTitle;
          $.garmentImage.hidden = false;
          if ($.garmentPlaceholder) $.garmentPlaceholder.hidden = true;
        } else if (!this.dataset.referenceImage) {
          $.garmentImage.hidden = true;
          if ($.garmentPlaceholder) $.garmentPlaceholder.hidden = false;
        }
      }
      this._renderResultNote();
    }

    /* ------------------------------------------------------------ dialog */

    _onLaunch() {
      if (this.dataset.customerLoggedIn !== "true") {
        this._redirectToLogin();
        return;
      }
      this._openDialog();
    }

    _openDialog() {
      if (this._state.lastResult) this._renderResult();
      this._hideToast();
      var dialog = this.$.dialog;
      if (typeof dialog.showModal === "function") {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute("open", "true");
      }
      this._closing = false;
      // The backdrop fade is a CSS transition on ::backdrop; adding the class
      // one frame after showModal lets the transition actually run.
      window.requestAnimationFrame(() => {
        if (dialog.open) dialog.classList.add("is-open");
      });
      this._playShellMotion(true);
      document.documentElement.classList.add("drezanielle-vto-open");
    }

    /* Sheet motion (apple-design §3, §7): one persistent WAAPI animation on
       the shell. Closing reverses the same animation, so enter and exit are
       symmetric by construction, and an Escape/backdrop click mid-flight
       reverses from the current on-screen value — no jump, no input lock.
       Only transform and opacity are animated. Reduced motion swaps the
       slide for a short opacity cross-fade. */
    _playShellMotion() {
      var dialog = this.$.dialog;
      var shell = dialog && dialog.querySelector(".drezanielle-vto__dialog-shell");
      if (!shell || typeof shell.animate !== "function") return null;
      if (this._shellAnim) {
        this._shellAnim.reverse();
        return this._shellAnim;
      }
      var reduce = false;
      var mobile = false;
      try {
        reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        mobile = window.matchMedia("(max-width: 700px)").matches;
      } catch { reduce = false; mobile = false; }
      var from = reduce
        ? { opacity: "0" }
        : mobile
          ? { opacity: "0", transform: "translateY(48px)" }
          : { opacity: "0", transform: "translateY(12px) scale(0.985)" };
      var to = reduce
        ? { opacity: "1" }
        : { opacity: "1", transform: "translateY(0) scale(1)" };
      this._shellAnim = shell.animate([from, to], {
        duration: reduce ? 120 : 240,
        easing: "cubic-bezier(0.32, 0.72, 0, 1)",
        fill: "both"
      });
      return this._shellAnim;
    }

    _closeDialog() {
      var dialog = this.$.dialog;
      if (!dialog || this._closing) return;
      if (typeof dialog.close === "function" && dialog.open) {
        this._closing = true;
        dialog.classList.remove("is-open");
        var anim = this._playShellMotion(false);
        var finish = () => {
          this._closing = false;
          if (dialog.open) dialog.close();
        };
        if (anim && anim.finished && typeof anim.finished.then === "function") {
          anim.finished.then(finish, finish);
        } else {
          finish();
        }
      } else if (typeof dialog.close !== "function") {
        dialog.classList.remove("is-open");
        dialog.removeAttribute("open");
      }
    }

    _onDialogCancel(event) {
      // Escape: take over from the native instant close so the exit travels
      // the same path as the entrance.
      event.preventDefault();
      this._closeDialog();
    }

    _onDialogClosed() {
      document.documentElement.classList.remove("drezanielle-vto-open");
      this._closing = false;
      if (this.$.dialog) this.$.dialog.classList.remove("is-open");
      if (this.$.launch) this.$.launch.focus();
      if (this._state.busy) this._showToast("progress");
    }

    _onDialogClick(event) {
      if (event.target === this.$.dialog) this._closeDialog();
    }

    /* ------------------------------------------------------------- photo */

    _onFileInput() {
      var files = this.$.fileInput.files;
      this._acceptFile(files && files[0]);
    }

    _onDragState(event) {
      event.preventDefault();
      if (this._state.busy) return;
      var active = event.type === "dragenter" || event.type === "dragover";
      this.$.dropzone.classList.toggle("is-dragging", active);
    }

    _onDrop(event) {
      if (this._state.busy) return;
      var files = event.dataTransfer && event.dataTransfer.files;
      if (files && files[0]) this._acceptFile(files[0]);
    }

    async _acceptFile(file) {
      if (!file) return;
      var $ = this.$;
      var token = (this._state.fileToken += 1);
      this._clearError();
      this._setPhotoNote("");
      if ($.fileLabel) $.fileLabel.textContent = this._t("upload.title_checking");
      this._updateSubmitState();

      var fail = function (message) {
        return { ok: false, message: message };
      };
      var result;
      if (ALLOWED_TYPES.indexOf(file.type) === -1) {
        result = fail(this._t("errors.photo_type"));
      } else if (file.size > MAX_PHOTO_BYTES) {
        result = fail(this._t("errors.photo_too_large"));
      } else {
        var dimensions = await decodePhoto(file);
        if (token !== this._state.fileToken) return;
        if (!dimensions) result = fail(this._t("errors.photo_undecodable"));
        else if (dimensions.width < MIN_PHOTO_DIMENSION || dimensions.height < MIN_PHOTO_DIMENSION) result = fail(this._t("errors.photo_too_small"));
        else result = { ok: true, landscape: dimensions.width > dimensions.height };
      }
      if (token !== this._state.fileToken) return;

      if (!result.ok) {
        this._clearFile();
        this._showError(result.message);
        return;
      }

      this._revokePreview();
      this._state.file = file;
      this._state.previewUrl = window.URL.createObjectURL(file);
      this._state.idempotencyKey = null;
      if ($.fileLabel) $.fileLabel.textContent = file.name;
      if ($.preview) {
        $.preview.src = this._state.previewUrl;
        $.preview.hidden = false;
      }
      if ($.dropzone) $.dropzone.classList.add("has-file");
      if (result.landscape) this._setPhotoNote(this._t("upload.portrait_note"));
      this._updateSubmitState();
    }

    _setPhotoNote(text) {
      if (!this.$.photoNote) return;
      this.$.photoNote.textContent = text;
      this.$.photoNote.hidden = !text;
    }

    _revokePreview() {
      if (this._state.previewUrl) window.URL.revokeObjectURL(this._state.previewUrl);
      this._state.previewUrl = null;
    }

    _clearFile() {
      this._state.file = null;
      this._state.fileToken += 1;
      this._revokePreview();
      var $ = this.$;
      if ($.fileInput) $.fileInput.value = "";
      if ($.fileLabel) $.fileLabel.textContent = this._t("upload.title");
      if ($.preview) {
        $.preview.hidden = true;
        $.preview.removeAttribute("src");
      }
      if ($.dropzone) $.dropzone.classList.remove("has-file");
      this._setPhotoNote("");
      this._updateSubmitState();
    }

    /* ------------------------------------------------------------ submit */

    _updateSubmitState() {
      var $ = this.$;
      if (!$.submit) return;
      $.submit.disabled = this._state.busy || !this._state.file || !($.consent && $.consent.checked);
    }

    _setBusy(value) {
      var $ = this.$;
      this._state.busy = value;
      $.submit.classList.toggle("is-busy", value);
      $.submit.setAttribute("aria-busy", value ? "true" : "false");
      if ($.submitLabel) $.submitLabel.textContent = value ? this._t("submit.busy") : this._t("submit.label");
      if ($.fileInput) $.fileInput.disabled = value;
      if ($.consent) $.consent.disabled = value;
      if ($.dropzone) {
        $.dropzone.classList.toggle("is-busy", value);
        $.dropzone.setAttribute("aria-disabled", value ? "true" : "false");
      }
      if ($.progress) $.progress.hidden = !value;
      if ($.waiting) $.waiting.hidden = !value;
      if ($.form) $.form.hidden = value || Boolean(this._state.lastResult);
      if ($.result) $.result.hidden = !this._state.lastResult;
      if (value) {
        this._startedAt = Date.now();
        this._renderProgress(0);
        this._timers.elapsed = window.setInterval(() => {
          this._renderProgress(Math.floor((Date.now() - this._startedAt) / 1000));
        }, 1000);
      } else {
        if (this._timers.elapsed) window.clearInterval(this._timers.elapsed);
        this._timers.elapsed = null;
        if ($.launchLabel) $.launchLabel.textContent = this._t("entry.launch");
      }
      this._updateSubmitState();
    }

    _renderProgress(seconds) {
      var $ = this.$;
      var phase = seconds < 12 ? 0 : seconds < 90 ? 1 : 2;
      var phaseKeys = ["progress.stage_upload", "progress.stage_tailoring", "progress.stage_finishing"];
      if ($.progressStatus) $.progressStatus.textContent = this._t("progress.status", { seconds: seconds });
      if ($.waitSeconds) $.waitSeconds.textContent = String(seconds);
      if ($.waitStage) $.waitStage.textContent = this._t(phaseKeys[phase]);
      if ($.launchLabel) $.launchLabel.textContent = this._t("entry.in_progress", { seconds: seconds });
      Array.prototype.forEach.call($.waitSteps || [], function (step, index) {
        step.classList.toggle("is-active", index === phase);
        step.classList.toggle("is-complete", index < phase);
      });
    }

    _clearTimers() {
      if (this._timers.timeout) window.clearTimeout(this._timers.timeout);
      if (this._timers.elapsed) window.clearInterval(this._timers.elapsed);
      this._timers.timeout = null;
      this._timers.elapsed = null;
    }

    async _pollGeneration(requestId, controller) {
      while (true) {
        await new Promise(function (resolve) { window.setTimeout(resolve, POLL_INTERVAL_MS); });
        var response = await window.fetch(this._apiUrl("/api/v1/try-on/") + encodeURIComponent(requestId), {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
          signal: controller.signal
        });
        var body = await response.json().catch(function () { return {}; });
        if (body.quota) this._setQuota(body.quota);
        if (!response.ok || body.status !== "processing") return { response: response, body: body };
      }
    }

    async _resumeStoredJob(job) {
      if (!job || this._state.busy || this._state.lastResult) return;
      this._abort = new AbortController();
      var controller = this._abort;
      this._setBusy(true);
      this._showToast("progress");
      try {
        var response = await window.fetch(this._apiUrl("/api/v1/try-on/") + encodeURIComponent(job.requestId), {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
          signal: controller.signal
        });
        var body = await response.json().catch(function () { return {}; });
        if (body.quota) this._setQuota(body.quota);
        if (response.ok && body.status === "processing") {
          var completed = await this._pollGeneration(job.requestId, controller);
          response = completed.response;
          body = completed.body;
        }
        if (!response.ok || body.status === "failed") {
          clearStoredJob(job.requestId);
          var code = body.error && body.error.code || body.errorCode;
          this._showError(this._t(ERROR_KEYS[code] || "errors.generic"));
          return;
        }
        if (body.imageDataUrl) {
          this._state.lastResult = {
            dataUrl: body.imageDataUrl,
            requestId: body.requestId || job.requestId,
            variantId: String(job.variantId || this.dataset.variantId || ""),
            variantTitle: job.variantTitle || this.dataset.variantTitle || ""
          };
          if (body.quota) this._setQuota(body.quota);
          clearStoredJob(job.requestId);
          this._renderResult();
        }
      } catch (error) {
        if (!(error && error.name === "AbortError")) this._showError(this._t("errors.connection_failed"));
      } finally {
        this._clearTimers();
        this._setBusy(false);
      }
    }

    async _onSubmit(event) {
      event.preventDefault();
      var state = this._state;
      if (state.busy || !state.file || !(this.$.consent && this.$.consent.checked)) return;
      this._clearError();
      this._setBusy(true);
      if (!state.idempotencyKey) state.idempotencyKey = makeIdempotencyKey();

      var payload = new FormData();
      payload.append("photo", state.file, state.file.name);
      payload.append("productId", this.dataset.productId || "");
      payload.append("variantId", this.dataset.variantId || "");
      payload.append("sku", this.dataset.sku || "");
      payload.append("productTitle", this.dataset.productTitle || "");
      payload.append("referenceImageUrl", this.dataset.referenceImage || "");
      payload.append("privacyConsent", "true");

      this._abort = new AbortController();
      var controller = this._abort;
      var timedOut = false;
      this._timers.timeout = window.setTimeout(function () {
        timedOut = true;
        controller.abort();
      }, SUBMIT_TIMEOUT_MS);

      try {
        var response = await window.fetch(this._apiUrl("/api/v1/try-on"), {
          method: "POST",
          headers: { "Idempotency-Key": state.idempotencyKey, Prefer: "respond-async", Accept: "application/json" },
          body: payload,
          credentials: "same-origin",
          signal: controller.signal
        });
        var body = await response.json().catch(function () { return {}; });
        if (body.quota) this._setQuota(body.quota);
        if (response.ok && body.status === "processing" && body.requestId) {
          writeStoredJob({
            requestId: body.requestId,
            status: "processing",
            startedAt: Date.now(),
            productId: String(this.dataset.productId || ""),
            productUrl: window.location.href,
            variantId: String(this.dataset.variantId || ""),
            variantTitle: this.dataset.variantTitle || ""
          });
          var completed = await this._pollGeneration(body.requestId, controller);
          response = completed.response;
          body = completed.body;
        }
        if (!response.ok) {
          var code = body.error && body.error.code;
          if (code === "CUSTOMER_LOGIN_REQUIRED") {
            this._redirectToLogin();
            return;
          }
          if (body.requestId) clearStoredJob(body.requestId);
          this._showError(this._t(ERROR_KEYS[code] || "errors.generic"));
          return;
        }
        if (body.status === "failed") {
          if (body.requestId) clearStoredJob(body.requestId);
          this._showError(this._t(ERROR_KEYS[body.errorCode] || "errors.generic"));
          return;
        }
        if (body.imageDataUrl) {
          state.lastResult = {
            dataUrl: body.imageDataUrl,
            requestId: body.requestId || null,
            variantId: String(this.dataset.variantId || ""),
            variantTitle: this.dataset.variantTitle || ""
          };
          clearStoredJob(body.requestId);
          this._renderResult();
        } else {
          this._showError(this._t("errors.generic"));
        }
      } catch (error) {
        if (timedOut || (error && error.name === "AbortError")) this._showError(this._t("errors.timeout"));
        else this._showError(this._t("errors.connection_failed"));
      } finally {
        // The attempt completed (success or failure): the next attempt gets a fresh key.
        state.idempotencyKey = null;
        this._clearTimers();
        this._setBusy(false);
      }
    }

    /* ------------------------------------------------------------ result */

    _renderResult() {
      var $ = this.$;
      var lastResult = this._state.lastResult;
      if (!lastResult) return;
      if ($.resultImage) $.resultImage.src = lastResult.dataUrl;
      if ($.resultPhoto) {
        if (this._state.previewUrl) {
          $.resultPhoto.src = this._state.previewUrl;
          $.resultPhoto.hidden = false;
        } else {
          $.resultPhoto.hidden = true;
        }
      }
      if ($.download) $.download.href = lastResult.dataUrl;
      this._renderResultNote();
      if ($.form) $.form.hidden = true;
      if ($.result) $.result.hidden = false;
      if ($.waiting) $.waiting.hidden = true;
      if ($.dialog && !$.dialog.open) this._showToast("success");
    }

    _renderResultNote() {
      var $ = this.$;
      if (!$.resultNote) return;
      var lastResult = this._state.lastResult;
      if (
        lastResult &&
        lastResult.variantId &&
        String(this.dataset.variantId || "") !== lastResult.variantId
      ) {
        var label = lastResult.variantTitle || "";
        $.resultNote.textContent = this._t("result.previous_variant", { title: label });
        $.resultNote.hidden = false;
      } else {
        $.resultNote.textContent = "";
        $.resultNote.hidden = true;
      }
    }

    _resetFitting() {
      this._state.lastResult = null;
      this._state.idempotencyKey = null;
      if (this.$.result) this.$.result.hidden = true;
      if (this.$.form) this.$.form.hidden = false;
      if (this.$.form) this.$.form.reset();
      this._clearFile();
      this._clearError();
      this._hideToast();
    }

    /* ------------------------------------------------------------- misc */

    _showError(message) {
      if (!this.$.error) return;
      this.$.error.textContent = message;
      this.$.error.hidden = !message;
      if (message && this.$.dialog && !this.$.dialog.open) this._showToast("error", message);
    }

    _clearError() {
      this._showError("");
    }

    _showToast(kind, detail) {
      var $ = this.$;
      if (!$.toast) return;
      var titleKey = kind === "success" ? "toast.success_title" : kind === "error" ? "toast.error_title" : "toast.progress_title";
      var bodyKey = kind === "success" ? "toast.success_body" : kind === "error" ? null : "toast.progress_body";
      $.toast.dataset.kind = kind;
      if ($.toastTitle) $.toastTitle.textContent = this._t(titleKey);
      if ($.toastBody) $.toastBody.textContent = detail || (bodyKey ? this._t(bodyKey) : "");
      if ($.toastAction) $.toastAction.textContent = this._t(kind === "error" ? "toast.try_again" : "toast.view");
      $.toast.hidden = false;
    }

    _hideToast() {
      if (this.$.toast) this.$.toast.hidden = true;
    }

    _onToastAction() {
      this._hideToast();
      this._openDialog();
    }
  }

  if (!window.customElements.get(TAG)) {
    window.customElements.define(TAG, DrezanielleVto);
  }

  // Theme editor: sections are re-rendered via shopify:section:load. Custom
  // elements reconnect on their own when re-inserted; this only nudges any
  // element that was parsed before the definition was registered.
  document.addEventListener("shopify:section:load", function (event) {
    var target = event.target;
    if (!target || typeof target.querySelectorAll !== "function") return;
    Array.prototype.forEach.call(target.querySelectorAll(TAG), function (element) {
      if (element.isConnected && !element._initialized && typeof element.connectedCallback === "function") {
        element.connectedCallback();
      }
    });
  });

  function scheduleStoredJobMonitor() {
    window.setTimeout(function () {
      globalMonitorStarted = false;
      monitorStoredJob();
    }, 0);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleStoredJobMonitor, { once: true });
  } else {
    scheduleStoredJobMonitor();
  }
  window.addEventListener("storage", function (event) {
    if (event.key === STORED_JOB_KEY) scheduleStoredJobMonitor();
  });
}());
